import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { fetchMFAPreference, updateMFAPreference } from "aws-amplify/auth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../lib/auth-context.js";
import { usePreferences } from "../lib/preferences.js";
import { listScenarios } from "../lib/api-modules/scenarios.js";
import {
  getMySettings,
  patchMySettings,
} from "../lib/api-modules/user-settings.js";
import {
  WidgetGrid,
  WidgetLibraryDrawer,
} from "../components/dashboard/widget-grid.js";
import {
  resolveLayout,
  type DashboardLayout,
} from "../lib/widgets/registry.js";
import {
  Card,
  SectionHeader,
} from "../components/design-system/index.js";
import { useToast } from "../components/toast.js";
import { SuggestedPromptsTile } from "../components/SuggestedPromptsTile.js";
import { PAGE_PROMPTS } from "../lib/data/page-prompts.js";

/**
 * Hearth dashboard — Phase 13.5 customizable widget framework.
 *
 * The IA review (DECISIONS.md 2026-05-09 IA review block) replaced the
 * fixed 15-tile stack with a per-user widget layout stored in
 * `user_settings.dashboard_layout`. The page chrome (greeting + ticker
 * search + suggested prompts) lives outside the grid; everything else
 * is a registered widget reorderable via "Customize" mode.
 *
 * FIRE numbers / withdrawal modeler / Social Security / geographic
 * arbitrage / Shockingly Simple Math all moved to standalone /fire/*
 * routes in 13.5 — they're not widgets, they're full pages. The
 * lifestyle-inflation tile lives at /spending#lifestyle-inflation but
 * is also registered as a (defaulted-hidden) widget so users can pin
 * it back to /dashboard if they want.
 */
export function DashboardPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const {
    privacyMode,
    readOnlyMode,
    togglePrivacyMode,
    toggleReadOnlyMode,
  } = usePreferences();
  const navigate = useNavigate();
  const [tickerInput, setTickerInput] = useState("");
  const [editing, setEditing] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    void (async () => {
      try {
        const prefs = await fetchMFAPreference();
        if (prefs.preferred !== "TOTP") {
          await updateMFAPreference({ totp: "PREFERRED" });
        }
      } catch (err) {
        console.warn("Failed to ensure TOTP MFA preference", err);
      }
    })();
  }, [user]);

  const settingsQuery = useQuery({
    queryKey: ["me-settings"],
    queryFn: getMySettings,
    enabled: !!user,
  });
  const layoutMutation = useMutation({
    mutationFn: (next: DashboardLayout | null) =>
      patchMySettings({ dashboard_layout: next }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me-settings"] });
    },
    onError: (err) => {
      toast.show(
        err instanceof Error
          ? `Couldn't save layout: ${err.message}`
          : "Couldn't save layout",
      );
    },
  });

  const layout = resolveLayout(
    (settingsQuery.data?.dashboard_layout) ?? null,
  );

  if (!user) return null;

  function onSearchChart(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const t = tickerInput.trim().toUpperCase();
    if (t.length === 0) return;
    setTickerInput("");
    void navigate(`/chart/${encodeURIComponent(t)}`);
  }

  function onLayoutChange(next: DashboardLayout) {
    layoutMutation.mutate(next);
  }

  function onAddWidget(widget_id: string) {
    const next: DashboardLayout = {
      widgets: layout.widgets.map((w) =>
        w.widget_id === widget_id ? { ...w, hidden: false } : w,
      ),
    };
    onLayoutChange(next);
    setDrawerOpen(false);
  }

  function onResetLayout() {
    layoutMutation.mutate(null);
    setDrawerOpen(false);
  }

  return (
    <div className="mx-auto max-w-6xl p-6 lg:p-8">
      <SectionHeader
        title={
          <>
            Welcome back,{" "}
            <span className="text-[var(--color-accent)]">{user.display_name}</span>
          </>
        }
        sub="Your dashboard — pin the widgets that matter to you."
        action={
          <div className="flex flex-wrap gap-1">
            <button
              type="button"
              onClick={togglePrivacyMode}
              aria-pressed={privacyMode}
              data-testid="privacy-toggle"
              className={
                privacyMode
                  ? "rounded-md bg-[var(--color-accent)] px-3 py-1.5 text-xs font-medium text-[var(--color-accent-contrast)]"
                  : "rounded-md border border-[var(--color-border-default)] bg-[var(--color-card)] px-3 py-1.5 text-xs font-medium text-[var(--color-text)] hover:bg-[var(--color-elevated)]"
              }
            >
              {privacyMode ? "Show values" : "Hide values"}
            </button>
            <button
              type="button"
              onClick={toggleReadOnlyMode}
              aria-pressed={readOnlyMode}
              data-testid="readonly-toggle"
              className={
                readOnlyMode
                  ? "rounded-md bg-[var(--color-accent)] px-3 py-1.5 text-xs font-medium text-[var(--color-accent-contrast)]"
                  : "rounded-md border border-[var(--color-border-default)] bg-[var(--color-card)] px-3 py-1.5 text-xs font-medium text-[var(--color-text)] hover:bg-[var(--color-elevated)]"
              }
            >
              {readOnlyMode ? "Read-only on" : "Read-only off"}
            </button>
            <button
              type="button"
              onClick={() => setEditing((e) => !e)}
              aria-pressed={editing}
              className={
                editing
                  ? "rounded-md bg-[var(--color-accent)] px-3 py-1.5 text-xs font-medium text-[var(--color-accent-contrast)]"
                  : "rounded-md border border-[var(--color-border-default)] bg-[var(--color-card)] px-3 py-1.5 text-xs font-medium text-[var(--color-text)] hover:bg-[var(--color-elevated)]"
              }
            >
              {editing ? "Done customizing" : "Customize"}
            </button>
            {editing && (
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className="rounded-md border border-[var(--color-border-default)] bg-[var(--color-card)] px-3 py-1.5 text-xs font-medium text-[var(--color-text)] hover:bg-[var(--color-elevated)]"
              >
                + Add widget
              </button>
            )}
          </div>
        }
      />

      <WidgetGrid
        layout={layout}
        editing={editing}
        onChange={onLayoutChange}
      />

      <ActiveScenariosPill />

      <Card className="mb-6">
        <p className="mb-3 text-xs font-medium uppercase tracking-wide text-[var(--color-muted)]">
          Quick chart
        </p>
        <form onSubmit={onSearchChart} className="flex max-w-sm gap-2">
          <input
            type="text"
            aria-label="View chart for a ticker"
            placeholder="Ticker (e.g., AAPL)"
            value={tickerInput}
            onChange={(e) => setTickerInput(e.target.value.toUpperCase())}
            className="flex-1 rounded-md border border-[var(--color-border-default)] bg-[var(--color-card)] px-3 py-2 text-sm text-[var(--color-text)] placeholder:text-[var(--color-faint)] focus:border-[var(--color-accent)] focus:outline-none focus:ring-1 focus:ring-[var(--color-accent)]"
          />
          <button
            type="submit"
            disabled={tickerInput.trim().length === 0}
            className="rounded-md border border-[var(--color-border-default)] bg-[var(--color-card)] px-3 py-2 text-sm font-medium text-[var(--color-text)] hover:bg-[var(--color-elevated)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            View chart
          </button>
        </form>
      </Card>

      <div className="mt-8">
        <SuggestedPromptsTile prompts={PAGE_PROMPTS.dashboard} />
      </div>

      <WidgetLibraryDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        layout={layout}
        onAdd={onAddWidget}
        onReset={onResetLayout}
      />
    </div>
  );
}

function ActiveScenariosPill() {
  const navigate = useNavigate();
  const scenariosQuery = useQuery({
    queryKey: ["scenarios", { includeArchived: false }],
    queryFn: () => listScenarios(false),
  });
  const count = scenariosQuery.data?.scenarios.length ?? 0;
  if (scenariosQuery.isLoading || scenariosQuery.isError || count === 0) {
    return null;
  }
  return (
    <Card className="mb-6 flex items-center justify-between">
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-muted)]">
          Saved scenarios
        </p>
        <p className="mt-1 text-sm text-[var(--color-text)]">
          You have <strong>{count}</strong> active{" "}
          {count === 1 ? "scenario" : "scenarios"} ready to replay.
        </p>
      </div>
      <button
        type="button"
        onClick={() => void navigate("/scenarios")}
        className="rounded-md border border-[var(--color-border-default)] bg-[var(--color-card)] px-3 py-2 text-sm font-medium text-[var(--color-text)] hover:bg-[var(--color-elevated)]"
      >
        Open Scenarios →
      </button>
    </Card>
  );
}
